import React from 'react';
import { useUI } from '../../context/UIContext';

// ============================================
// Types
// ============================================

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastConfig {
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

// ============================================
// Toast Configurations
// ============================================

const toastConfigs: Record<ToastType, ToastConfig> = {
  success: {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-100',
  },
  error: {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-700',
    borderColor: 'border-rose-100',
  },
  info: {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-700',
    borderColor: 'border-indigo-100',
  },
  warning: {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-100',
  },
};

// ============================================
// Toast Component
// ============================================

export const Toast: React.FC = () => {
  const { feedback, clearFeedback } = useUI();

  if (!feedback) return null;

  const config = toastConfigs[feedback.type];

  return (
    <div className="fixed bottom-6 right-6 z-[200] animate-in slide-in-from-bottom-4 duration-300">
      <div 
        className={`
          flex items-center gap-3 
          px-5 py-4 
          rounded-2xl 
          shadow-xl 
          border
          ${config.bgColor}
          ${config.textColor}
          ${config.borderColor}
        `}
      >
        <div className="shrink-0">
          {config.icon}
        </div>
        <p className="text-sm font-bold pr-2">{feedback.message}</p>
        <button
          onClick={clearFeedback}
          className="shrink-0 p-1 hover:bg-black/5 rounded-lg transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;
